import { Grid, InputNumber, Span } from "@goodfill/ui-components";
import { useFormContext, Controller } from "react-hook-form";
import { useRecoilState } from "recoil";
import { CountryInput } from "../../../../../../countries/CountryInput";
import { FieldGroup } from "../../../../../../form/FieldGroup";
import { CustomerApplicationFormState } from "../../../formState/formState";
import { FormYesOrNoQuestion } from "../../FormYesOrNoQuestion";
import { SignatureBox } from "../../SignatureBox";
import { validStep1State } from "../Step1SignUp/step1SignUpState";

const formatValueSecurityNumber = (value: unknown) =>
  Array.from({ length: String(value).length })
    .fill("•")
    .join("");

const promptMessage = `
I acknowledge that the foregoing information and all other information provided 
during the account application process is true and correct and agree to notify 
GoodFill LLC by email of any material changes therein. I authorized 
GoodFill LLC to confirm the accuracy of the information as it deems necessary.
`;
export const Step3Personal = ({ areAllDisabled }: { areAllDisabled: boolean }) => {
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext<CustomerApplicationFormState>();

  const [validStep1] = useRecoilState(validStep1State);

  return (
    <div className="flex flex-col px-4 md:px-8 py-4 md:py-8 border-b border-neutral-110 items-center">
      <Span size="2xl" uppercase={false} font="semibold">
        Personal information
      </Span>

      <Span className="Steps_welcomeMessage" wrap={true}>
        To open a free account and start you investing safely, we need to know more about you. Let's get started!
      </Span>

      <Grid cols={2} gap={0.75} className="w-full">
        <Controller
          render={({ field, fieldState: { error } }) => (
            <FieldGroup
              label="Social Security Number"
              validation={error?.message ? [error.message] : []}
              className="w-80-sm w-full-after-md"
              fill
            >
              <InputNumber
                maxLength={9}
                placeholder="Your Security Number"
                value={field.value}
                setValue={(value) => field.onChange(`${value}`)}
                formatValue={formatValueSecurityNumber}
                border={error === undefined ? "neutral" : "red"}
                onBlur={field.onBlur}
                ref={field.ref}
                id="socialSecurityNumber"
                disabled={areAllDisabled}
              />
            </FieldGroup>
          )}
          name="step3.socialSecurityNumber"
          control={control}
        />
      </Grid>
      <Grid cols={2} gap={0.75} className="w-full">
        <Controller
          render={({ field, fieldState: { error } }) => (
            <CountryInput
              title="Country of Birth"
              selected={field.value}
              onChange={field.onChange}
              validation={errors.step3?.countryOfBirth}
              onBlur={field.onBlur}
              ref={field.ref}
              id="countryOfBirth"
              disabled={areAllDisabled}
            />
          )}
          name="step3.countryOfBirth"
          control={control}
        />

        <Controller
          render={({ field, fieldState: { error } }) => (
            <CountryInput
              title="Country of Citizenship"
              selected={field.value}
              onChange={field.onChange}
              validation={errors.step3?.countryOfCitizenship}
              onBlur={field.onBlur}
              ref={field.ref}
              id="countryOfCitizenship"
              disabled={areAllDisabled}
            />
          )}
          name="step3.countryOfCitizenship"
          control={control}
        />
      </Grid>
      <Grid cols={2} gap={0.75} className="w-full">
        <Controller
          render={({ field, fieldState: { error } }) => (
            <FormYesOrNoQuestion
              id={"individualApplyingAcknowledgement"}
              label={"I certify I that I am applying for this account as an individual for tax calculation purposes"}
              value={field.value}
              onChange={field.onChange}
              validation={error?.message ? [error.message] : []}
              onBlur={field.onBlur}
              ref={field.ref}
              disabled={areAllDisabled}
              className="w-80-sm w-full-after-md"
            />
          )}
          name="step3.individualApplyingAcknowledgement"
          control={control}
        />

        <Controller
          render={({ field, fieldState: { error } }) => (
            <FormYesOrNoQuestion
              id={"ssnAcknowledgement"}
              label={"The number shown is my correct social security number"}
              value={field.value}
              onChange={field.onChange}
              validation={error?.message ? [error.message] : []}
              onBlur={field.onBlur}
              className="w-80-sm w-full-after-md"
              ref={field.ref}
              disabled={areAllDisabled}
            />
          )}
          name="step3.ssnAcknowledgement"
          control={control}
        />
      </Grid>

      <div style={{ maxWidth: "704px", width: "100%" }} className="flex w-80-sm">
        <Controller
          render={({ field, fieldState: { error } }) => (
            <FormYesOrNoQuestion
              id={"ssnWithholdingAcknowledgement"}
              label={
                "I am not subject to backup withholding because a) I am exempt from backup withholding, or b) I have not been notified by the Internal Revenue Service (IRS) that I am subject to backup withholding as a result of failure to report all interest or dividends, or c) the IRS has notified me that I am no longer subject to backup withholding"
              }
              value={field.value}
              onChange={field.onChange}
              validation={error?.message ? [error.message] : []}
              onBlur={field.onBlur}
              ref={field.ref}
              disabled={areAllDisabled}
              className="w-full-after-md w-80-sm w-full-before-md"
            />
          )}
          name="step3.ssnWithholdingAcknowledgement"
          control={control}
        />
      </div>

      <div style={{ maxWidth: "704px", width: "100%" }} className="flex w-80-sm">
        <Controller
          render={({ field, fieldState: { error } }) => (
            <FormYesOrNoQuestion
              id={"usaTaxResidency"}
              label="Are you a US tax resident?"
              value={field.value}
              onChange={field.onChange}
              validation={error?.message ? [error.message] : []}
              onBlur={field.onBlur}
              ref={field.ref}
              disabled={areAllDisabled}
              className="w-80-sm w-full-after-md w-full-before-md"
            />
          )}
          name="step3.usaTaxResidency"
          control={control}
        />
      </div>

      <div style={{ maxWidth: "704px", width: "100%" }} className="flex w-80-sm">
        <Controller
          render={({ field, fieldState: { error } }) => (
            <FieldGroup
              label="Signature"
              validation={error?.message ? [error.message] : []}
              fill={true}
              className="w-full-after-md w-80-sm w-full-before-md"
            >
              <SignatureBox
                promptMessage={promptMessage}
                value={field.value ?? ""}
                firstName={validStep1?.firstName ?? ""}
                lastName={validStep1?.lastName ?? ""}
                setValue={field.onChange}
                isValid={error === undefined}
                onBlur={field.onBlur}
                ref={field.ref}
                id={"trueAndCorrectSignature"}
                disabled={areAllDisabled}
              />
            </FieldGroup>
          )}
          name="step3.trueAndCorrectSignature"
          control={control}
        />
      </div>
    </div>
  );
};
